import React, { useState, useRef, useEffect } from 'react';
import { ArrowRight, Heart, Star, Truck, RefreshCw, Shield, Zap, MapPin, Instagram, ChevronRight, Play, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/**
 * Template 12: VENUE
 * Style: Bold, Urban, Contemporary
 * Best for: Urban streetwear, Contemporary Indian fashion, Metro city style
 * Light theme with bold orange/coral accents
 */
const Template12 = () => {
    const navigate = useNavigate();
    const [hoveredProduct, setHoveredProduct] = useState(null);
    const [activeTestimonial, setActiveTestimonial] = useState(0);
    const [wishlist, setWishlist] = useState({});
    const touchStartX = useRef(0);

    // Categories - Urban fashion
    const categories = [
        { name: 'Street Kurtas', tag: 'Trending', image: 'https://placehold.co/600x800/FFF7ED/EA580C?text=Street+Kurtas' },
        { name: 'Urban Denim', tag: 'New', image: 'https://placehold.co/600x800/FEF3C7/D97706?text=Urban+Denim' },
        { name: 'Fusion Jackets', tag: 'Hot', image: 'https://placehold.co/600x800/FFEDD5/C2410C?text=Fusion+Jackets' },
        { name: 'Metro Casuals', tag: 'Popular', image: 'https://placehold.co/600x800/FED7AA/9A3412?text=Metro+Casuals' }
    ];

    // Products - Urban Indian fashion
    const products = [
        { id: 1, name: 'Graffiti Print Kurta', price: '\u20B92,499', originalPrice: '\u20B93,999', image: 'https://placehold.co/400x500/FFF7ED/EA580C?text=Kurta+1', badge: 'Bestseller' },
        { id: 2, name: 'Distressed Denim Jacket', price: '\u20B93,999', originalPrice: '\u20B95,499', image: 'https://placehold.co/400x500/FEF3C7/D97706?text=Jacket+1', badge: 'New' },
        { id: 3, name: 'Oversized Graphic Tee', price: '\u20B91,299', originalPrice: '\u20B91,799', image: 'https://placehold.co/400x500/FFEDD5/C2410C?text=Tee+1', badge: 'Trending' },
        { id: 4, name: 'Cargo Joggers', price: '\u20B92,199', originalPrice: '\u20B92,999', image: 'https://placehold.co/400x500/FED7AA/9A3412?text=Joggers+1', badge: 'Hot' },
        { id: 5, name: 'Bomber Jacket Fusion', price: '\u20B94,499', originalPrice: '\u20B95,999', image: 'https://placehold.co/400x500/FFF7ED/EA580C?text=Bomber+1', badge: 'Premium' },
        { id: 6, name: 'Printed Nehru Collar', price: '\u20B91,899', originalPrice: '\u20B92,499', image: 'https://placehold.co/400x500/FEF3C7/D97706?text=Nehru+1', badge: 'Festive' }
    ];

    // City collections
    const cityCollections = [
        { city: 'Mumbai', vibe: 'Maximum City Style', image: 'https://placehold.co/400x500/FFF7ED/EA580C?text=Mumbai' },
        { city: 'Delhi', vibe: 'Capital Swagger', image: 'https://placehold.co/400x500/FEF3C7/D97706?text=Delhi' },
        { city: 'Bangalore', vibe: 'Tech City Cool', image: 'https://placehold.co/400x500/FFEDD5/C2410C?text=Bangalore' }
    ];

    // Testimonials
    const testimonials = [
        { text: 'Finally a brand that gets Indian street style. Their fusion wear is exactly what I was looking for!', name: 'Aryan Khanna', location: 'Mumbai', rating: 5 },
        { text: 'The quality is amazing and the designs are so unique. Perfect for college and hangouts.', name: 'Rohan Mehta', location: 'Delhi', rating: 5 },
        { text: 'Love how they blend traditional elements with urban aesthetics. True Indian streetwear!', name: 'Karan Singh', location: 'Bangalore', rating: 5 }
    ];

    // Mobile testimonial auto-rotate
    useEffect(() => {
        const timer = setInterval(() => {
            setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
        }, 5000);
        return () => clearInterval(timer);
    }, []);

    const handleTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
    const handleTouchEnd = (e) => {
        const diff = touchStartX.current - e.changedTouches[0].clientX;
        if (Math.abs(diff) > 50) {
            setActiveTestimonial((prev) => diff > 0 ? (prev + 1) % testimonials.length : (prev - 1 + testimonials.length) % testimonials.length);
        }
    };

    const toggleWishlist = (e, id) => { e.stopPropagation(); setWishlist((prev) => ({ ...prev, [id]: !prev[id] })); };

    return (
        <div className="w-full min-h-screen bg-white font-sans">
            {/* ===== HERO SECTION ===== */}
            {/* Mobile Hero: stacked */}
            <section className="lg:hidden">
                <div className="relative aspect-[4/5] w-full overflow-hidden bg-gradient-to-br from-orange-50 via-amber-50 to-orange-100">
                    <div className="grid grid-cols-2 gap-2 p-5 h-full">
                        <div className="space-y-2">
                            <div className="aspect-[3/4] overflow-hidden bg-orange-200 rounded-lg shadow-xl">
                                <img src="https://placehold.co/400x500/FFF7ED/EA580C?text=Look+1" alt="Urban Look" className="w-full h-full object-cover" />
                            </div>
                        </div>
                        <div className="space-y-2 pt-6">
                            <div className="aspect-[3/4] overflow-hidden bg-amber-300 rounded-lg shadow-xl">
                                <img src="https://placehold.co/400x500/FED7AA/9A3412?text=Look+4" alt="Urban Look" className="w-full h-full object-cover" />
                            </div>
                        </div>
                    </div>
                    {/* Floating Tag */}
                    <div className="absolute left-4 top-8 bg-orange-600 text-white px-3 py-1.5 rounded shadow-lg z-20 transform -rotate-3">
                        <span className="font-black text-xs">NEW DROP</span>
                    </div>
                </div>
                <div className="bg-orange-600 px-5 py-8 text-white">
                    <div className="flex items-center gap-2 mb-3">
                        <Zap className="w-4 h-4" />
                        <span className="text-xs font-bold tracking-wider uppercase">Urban Collection</span>
                    </div>
                    <h1 className="text-3xl font-black leading-[0.9] mb-3 tracking-tight">
                        OWN THE
                        <span className="block text-orange-200">STREETS</span>
                    </h1>
                    <p className="text-orange-100/80 text-[14px] leading-relaxed mb-6">
                        Bold. Fearless. Unapologetically Indian. Street style that speaks your language.
                    </p>
                    <button
                        onClick={() => navigate('/template12/shop')}
                        className="w-full py-4 bg-white text-orange-600 text-sm font-bold tracking-wider uppercase flex items-center justify-center gap-3"
                    >
                        Shop Now <ArrowRight className="w-4 h-4" />
                    </button>
                    <div className="flex justify-between mt-6 pt-5 border-t border-orange-500/50">
                        <div>
                            <p className="text-2xl font-black">500K+</p>
                            <p className="text-xs text-orange-200/70">Customers</p>
                        </div>
                        <div>
                            <p className="text-2xl font-black">50+</p>
                            <p className="text-xs text-orange-200/70">Cities</p>
                        </div>
                        <div>
                            <p className="text-2xl font-black">4.8</p>
                            <p className="text-xs text-orange-200/70">Rating</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Desktop Hero: Bold Typography - EXACT ORIGINAL */}
            <section className="hidden lg:block relative min-h-screen overflow-hidden bg-gradient-to-br from-orange-50 via-amber-50 to-orange-100">
                {/* Geometric Shapes */}
                <div className="absolute top-20 right-10 w-64 h-64 bg-orange-200/50 rounded-full blur-3xl" />
                <div className="absolute bottom-20 left-10 w-80 h-80 bg-amber-200/50 rounded-full blur-3xl" />

                {/* Grid Pattern */}
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute inset-0" style={{
                        backgroundImage: 'linear-gradient(to right, #EA580C 1px, transparent 1px), linear-gradient(to bottom, #EA580C 1px, transparent 1px)',
                        backgroundSize: '60px 60px'
                    }} />
                </div>

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <div className="grid lg:grid-cols-2 gap-12 items-center min-h-screen py-16">
                        {/* Text Content */}
                        <div className="text-center lg:text-left">
                            <div className="inline-flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-full mb-6">
                                <Zap className="w-4 h-4" />
                                <span className="text-sm font-bold tracking-wider uppercase">Urban Collection</span>
                            </div>
                            <h1 className="text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-black text-orange-950 leading-[0.9] mb-6 tracking-tight">
                                OWN THE
                                <span className="block text-orange-600">STREETS</span>
                            </h1>
                            <p className="text-orange-800/70 text-lg lg:text-xl max-w-lg mx-auto lg:mx-0 mb-8 leading-relaxed">
                                Bold. Fearless. Unapologetically Indian.
                                Street style that speaks your language.
                            </p>
                            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                                <button
                                    onClick={() => navigate('/template12/shop')}
                                    className="group px-8 py-4 bg-orange-600 text-white text-sm font-bold tracking-wider uppercase hover:bg-orange-700 transition-all duration-300 rounded-none flex items-center justify-center gap-3"
                                >
                                    Shop Now
                                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                </button>
                                <button className="group px-8 py-4 border-2 border-orange-600 text-orange-600 text-sm font-bold tracking-wider uppercase hover:bg-orange-600 hover:text-white transition-all duration-300 rounded-none flex items-center justify-center gap-3">
                                    <Play className="w-4 h-4" />
                                    Watch Film
                                </button>
                            </div>

                            {/* Stats */}
                            <div className="flex justify-center lg:justify-start gap-8 mt-12 pt-8 border-t border-orange-200">
                                <div>
                                    <p className="text-4xl font-black text-orange-600">500K+</p>
                                    <p className="text-sm text-orange-700/70 font-medium">Happy Customers</p>
                                </div>
                                <div>
                                    <p className="text-4xl font-black text-orange-600">50+</p>
                                    <p className="text-sm text-orange-700/70 font-medium">Cities</p>
                                </div>
                                <div>
                                    <p className="text-4xl font-black text-orange-600">4.8</p>
                                    <p className="text-sm text-orange-700/70 font-medium">Rating</p>
                                </div>
                            </div>
                        </div>

                        {/* Hero Image Collage */}
                        <div className="relative">
                            <div className="grid grid-cols-2 gap-4 max-w-lg mx-auto">
                                <div className="space-y-4">
                                    <div className="aspect-[3/4] overflow-hidden bg-orange-200 rounded-lg shadow-xl">
                                        <img
                                            src="https://placehold.co/400x500/FFF7ED/EA580C?text=Look+1"
                                            alt="Urban Look"
                                            className="w-full h-full object-cover"
                                        />
                                    </div>
                                    <div className="aspect-square overflow-hidden bg-amber-200 rounded-lg shadow-xl">
                                        <img
                                            src="https://placehold.co/300x300/FEF3C7/D97706?text=Look+2"
                                            alt="Urban Look"
                                            className="w-full h-full object-cover"
                                        />
                                    </div>
                                </div>
                                <div className="space-y-4 pt-8">
                                    <div className="aspect-square overflow-hidden bg-orange-300 rounded-lg shadow-xl">
                                        <img
                                            src="https://placehold.co/300x300/FFEDD5/C2410C?text=Look+3"
                                            alt="Urban Look"
                                            className="w-full h-full object-cover"
                                        />
                                    </div>
                                    <div className="aspect-[3/4] overflow-hidden bg-amber-300 rounded-lg shadow-xl">
                                        <img
                                            src="https://placehold.co/400x500/FED7AA/9A3412?text=Look+4"
                                            alt="Urban Look"
                                            className="w-full h-full object-cover"
                                        />
                                    </div>
                                </div>
                            </div>
                            {/* Floating Tag */}
                            <div className="absolute -left-4 top-1/3 bg-orange-600 text-white px-4 py-2 rounded shadow-lg z-20 transform -rotate-3">
                                <span className="font-black text-sm">NEW DROP</span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* ===== BOLD STATEMENT (Marquee) ===== */}
            <section className="py-10 lg:py-20 bg-orange-600 text-white overflow-hidden">
                <div className="max-w-7xl mx-auto px-4">
                    <div className="flex animate-marquee whitespace-nowrap">
                        {['STREET STYLE', 'URBAN CULTURE', 'BOLD MOVES', 'DESI SWAG', 'METRO VIBES'].map((text, index) => (
                            <span key={index} className="text-2xl lg:text-6xl font-black mx-4 lg:mx-8 opacity-50">{text} &bull;</span>
                        ))}
                        {['STREET STYLE', 'URBAN CULTURE', 'BOLD MOVES', 'DESI SWAG', 'METRO VIBES'].map((text, index) => (
                            <span key={`repeat-${index}`} className="text-2xl lg:text-6xl font-black mx-4 lg:mx-8 opacity-50">{text} &bull;</span>
                        ))}
                    </div>
                </div>
            </section>

            {/* ===== CATEGORIES ===== */}
            {/* Mobile: horizontal scroll */}
            <section className="lg:hidden py-10 bg-white">
                <div className="px-5">
                    <span className="text-orange-600 text-xs font-bold tracking-[0.2em] uppercase">Categories</span>
                    <h2 className="text-2xl font-black text-orange-950 mt-1 mb-5">Shop The Vibe</h2>
                    <div className="relative">
                        <div className="flex overflow-x-auto gap-3 snap-x snap-mandatory scrollbar-hide pb-2">
                            {categories.map((category, index) => (
                                <div
                                    key={index}
                                    onClick={() => navigate('/template12/shop')}
                                    className="flex-shrink-0 w-[68vw] snap-start cursor-pointer group"
                                >
                                    <div className="relative aspect-[3/4] overflow-hidden bg-orange-100">
                                        <img
                                            src={category.image}
                                            alt={category.name}
                                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-orange-950/80 via-transparent to-transparent" />
                                        <div className="absolute top-3 right-3">
                                            <span className="px-2 py-0.5 bg-orange-500 text-white text-[10px] font-bold rounded-full">
                                                {category.tag}
                                            </span>
                                        </div>
                                        <div className="absolute bottom-0 left-0 right-0 p-4">
                                            <h3 className="text-white text-xl font-black">{category.name}</h3>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="absolute right-0 top-0 bottom-2 w-10 bg-gradient-to-l from-white to-transparent pointer-events-none" />
                    </div>
                </div>
            </section>

            {/* Desktop: Categories - EXACT ORIGINAL */}
            <section className="hidden lg:block py-16 lg:py-24 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between mb-12 lg:mb-16">
                        <div>
                            <span className="text-orange-600 text-sm font-bold tracking-[0.2em] uppercase">Categories</span>
                            <h2 className="text-4xl lg:text-5xl font-black text-orange-950 mt-2">Shop The Vibe</h2>
                        </div>
                        <button
                            onClick={() => navigate('/template12/shop')}
                            className="group flex items-center gap-2 text-orange-600 font-bold mt-4 lg:mt-0 hover:text-orange-700 transition-colors"
                        >
                            View All
                            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                        </button>
                    </div>

                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
                        {categories.map((category, index) => (
                            <div
                                key={index}
                                onClick={() => navigate('/template12/shop')}
                                className="group cursor-pointer relative overflow-hidden"
                            >
                                <div className="aspect-[3/4] overflow-hidden bg-orange-100">
                                    <img
                                        src={category.image}
                                        alt={category.name}
                                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                    />
                                </div>
                                <div className="absolute inset-0 bg-gradient-to-t from-orange-950/80 via-transparent to-transparent" />
                                <div className="absolute top-4 right-4">
                                    <span className="px-3 py-1 bg-orange-500 text-white text-xs font-bold rounded-full">
                                        {category.tag}
                                    </span>
                                </div>
                                <div className="absolute bottom-0 left-0 right-0 p-4 lg:p-6">
                                    <h3 className="text-white text-xl lg:text-2xl font-black">{category.name}</h3>
                                    <div className="flex items-center gap-2 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <span className="text-orange-300 text-sm font-bold">Explore</span>
                                        <ArrowRight className="w-4 h-4 text-orange-300" />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ===== FEATURED PRODUCTS ===== */}
            {/* Mobile: horizontal scroll with wishlist/quick-add */}
            <section className="lg:hidden py-10 bg-orange-50">
                <div className="px-5">
                    <div className="text-center mb-5">
                        <span className="text-orange-600 text-xs font-bold tracking-[0.2em] uppercase">Just Dropped</span>
                        <h2 className="text-2xl font-black text-orange-950 mt-1">Fresh Off The Rack</h2>
                    </div>
                    <div className="flex overflow-x-auto gap-3 snap-x snap-mandatory scrollbar-hide pb-2">
                        {products.map((product) => (
                            <div
                                key={product.id}
                                onClick={() => navigate('/template12/shop')}
                                className="flex-shrink-0 w-[44vw] snap-start cursor-pointer group"
                            >
                                <div className="relative aspect-[4/5] overflow-hidden bg-white mb-2 shadow-sm">
                                    <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
                                    <span className="absolute top-2 left-2 px-2 py-0.5 text-[10px] font-bold bg-orange-600 text-white">
                                        {product.badge}
                                    </span>
                                    <button
                                        onClick={(e) => toggleWishlist(e, product.id)}
                                        className="absolute top-2 right-2 w-8 h-8 bg-white/90 rounded-full flex items-center justify-center"
                                    >
                                        <Heart className={`w-4 h-4 ${wishlist[product.id] ? 'fill-orange-600 text-orange-600' : 'text-orange-400'}`} />
                                    </button>
                                    <button className="absolute bottom-2 right-2 w-8 h-8 bg-white/90 rounded-full flex items-center justify-center shadow-md">
                                        <Plus className="w-4 h-4 text-orange-700" />
                                    </button>
                                </div>
                                <h3 className="text-orange-950 text-xs font-bold line-clamp-1">{product.name}</h3>
                                <div className="flex items-center gap-1.5 mt-0.5">
                                    <span className="text-orange-600 text-sm font-black">{product.price}</span>
                                    <span className="text-orange-400 text-xs line-through">{product.originalPrice}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                    <button
                        onClick={() => navigate('/template12/shop')}
                        className="w-full mt-5 py-3.5 bg-orange-600 text-white text-sm font-bold tracking-wider uppercase"
                    >
                        View All Products
                    </button>
                </div>
            </section>

            {/* Desktop: Products grid - EXACT ORIGINAL */}
            <section className="hidden lg:block py-16 lg:py-24 bg-orange-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12 lg:mb-16">
                        <span className="text-orange-600 text-sm font-bold tracking-[0.2em] uppercase">Just Dropped</span>
                        <h2 className="text-4xl lg:text-5xl font-black text-orange-950 mt-2">Fresh Off The Rack</h2>
                    </div>

                    <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-8">
                        {products.map((product) => (
                            <div
                                key={product.id}
                                onClick={() => navigate('/template12/shop')}
                                onMouseEnter={() => setHoveredProduct(product.id)}
                                onMouseLeave={() => setHoveredProduct(null)}
                                className="group cursor-pointer bg-white overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300"
                            >
                                <div className="relative aspect-[4/5] overflow-hidden">
                                    <img
                                        src={product.image}
                                        alt={product.name}
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                    />
                                    <span className="absolute top-3 left-3 px-3 py-1 bg-orange-600 text-white text-xs font-bold">
                                        {product.badge}
                                    </span>
                                    <button className="absolute top-3 right-3 w-10 h-10 bg-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 hover:bg-orange-50">
                                        <Heart className="w-5 h-5 text-orange-600" />
                                    </button>
                                    <div className={`absolute bottom-0 left-0 right-0 p-4 bg-orange-600 transform transition-transform duration-300 ${hoveredProduct === product.id ? 'translate-y-0' : 'translate-y-full'}`}>
                                        <button className="w-full py-2 text-white font-bold text-sm">
                                            ADD TO CART
                                        </button>
                                    </div>
                                </div>
                                <div className="p-4 lg:p-6">
                                    <h3 className="text-orange-950 font-bold group-hover:text-orange-600 transition-colors">
                                        {product.name}
                                    </h3>
                                    <div className="flex items-center gap-2 mt-2">
                                        <span className="text-orange-600 font-black text-lg">{product.price}</span>
                                        <span className="text-orange-400 text-sm line-through">{product.originalPrice}</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ===== CITY COLLECTIONS ===== */}
            {/* Mobile: horizontal scroll */}
            <section className="lg:hidden py-10 bg-white">
                <div className="px-5">
                    <div className="text-center mb-5">
                        <span className="text-orange-600 text-xs font-bold tracking-[0.2em] uppercase">Location Special</span>
                        <h2 className="text-2xl font-black text-orange-950 mt-1">Your City, Your Style</h2>
                    </div>
                    <div className="relative">
                        <div className="flex overflow-x-auto gap-3 snap-x snap-mandatory scrollbar-hide pb-2">
                            {cityCollections.map((city, index) => (
                                <div
                                    key={index}
                                    onClick={() => navigate('/template12/shop')}
                                    className="flex-shrink-0 w-[75vw] snap-start cursor-pointer group relative overflow-hidden aspect-[4/5] rounded-lg"
                                >
                                    <img
                                        src={city.image}
                                        alt={city.city}
                                        className="w-full h-full object-cover"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-orange-950/90 via-orange-900/40 to-transparent" />
                                    <div className="absolute bottom-0 left-0 right-0 p-5">
                                        <div className="flex items-center gap-2 text-orange-300 mb-1">
                                            <MapPin className="w-3 h-3" />
                                            <span className="text-xs font-medium">{city.vibe}</span>
                                        </div>
                                        <h3 className="text-white text-3xl font-black">{city.city}</h3>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="absolute right-0 top-0 bottom-2 w-10 bg-gradient-to-l from-white to-transparent pointer-events-none" />
                    </div>
                </div>
            </section>

            {/* Desktop: City Collections - EXACT ORIGINAL */}
            <section className="hidden lg:block py-16 lg:py-24 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12 lg:mb-16">
                        <span className="text-orange-600 text-sm font-bold tracking-[0.2em] uppercase">Location Special</span>
                        <h2 className="text-4xl lg:text-5xl font-black text-orange-950 mt-2">Your City, Your Style</h2>
                    </div>

                    <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
                        {cityCollections.map((city, index) => (
                            <div
                                key={index}
                                onClick={() => navigate('/template12/shop')}
                                className="group cursor-pointer relative overflow-hidden aspect-[4/5]"
                            >
                                <img
                                    src={city.image}
                                    alt={city.city}
                                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-orange-950/90 via-orange-900/40 to-transparent" />
                                <div className="absolute bottom-0 left-0 right-0 p-8">
                                    <div className="flex items-center gap-2 text-orange-300 mb-2">
                                        <MapPin className="w-4 h-4" />
                                        <span className="text-sm font-medium">{city.vibe}</span>
                                    </div>
                                    <h3 className="text-white text-4xl lg:text-5xl font-black">{city.city}</h3>
                                    <button className="mt-4 text-white font-bold text-sm flex items-center gap-2 group-hover:gap-4 transition-all">
                                        Shop Collection
                                        <ArrowRight className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ===== TESTIMONIALS ===== */}
            {/* Mobile: swipeable carousel */}
            <section className="lg:hidden py-10 bg-orange-950 text-white">
                <div className="px-5">
                    <span className="text-orange-400 text-xs font-bold tracking-[0.2em] uppercase">Word On The Street</span>
                    <h2 className="text-2xl font-black text-white mt-1 mb-5">Real Talk</h2>
                    <div className="overflow-hidden" onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
                        <div className="flex transition-transform duration-500 ease-out" style={{ transform: `translateX(-${activeTestimonial * 100}%)` }}>
                            {testimonials.map((testimonial, index) => (
                                <div key={index} className="w-full flex-shrink-0 px-1">
                                    <div className="bg-orange-900/50 p-6 rounded-lg">
                                        <div className="flex gap-1 mb-3">
                                            {[...Array(testimonial.rating)].map((_, i) => (
                                                <Star key={i} className="w-3.5 h-3.5 fill-orange-400 text-orange-400" />
                                            ))}
                                        </div>
                                        <p className="text-orange-100 text-[14px] leading-relaxed mb-5">
                                            "{testimonial.text}"
                                        </p>
                                        <div className="flex items-center gap-3 pt-4 border-t border-orange-800">
                                            <div className="w-10 h-10 bg-orange-600 rounded-full flex items-center justify-center">
                                                <span className="text-white font-bold text-sm">{testimonial.name[0]}</span>
                                            </div>
                                            <div>
                                                <p className="font-bold text-white text-sm">{testimonial.name}</p>
                                                <p className="text-orange-400 text-xs">{testimonial.location}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="flex justify-center gap-2 mt-5">
                            {testimonials.map((_, i) => (
                                <button
                                    key={i}
                                    onClick={() => setActiveTestimonial(i)}
                                    className={`h-2 rounded-full transition-all duration-300 ${activeTestimonial === i ? 'w-6 bg-orange-500' : 'w-2 bg-orange-800'}`}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Desktop: Testimonials - EXACT ORIGINAL */}
            <section className="hidden lg:block py-16 lg:py-24 bg-orange-950 text-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-12 lg:mb-16">
                        <span className="text-orange-400 text-sm font-bold tracking-[0.2em] uppercase">Word On The Street</span>
                        <h2 className="text-4xl lg:text-5xl font-black text-white mt-2">Real Talk</h2>
                    </div>

                    <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
                        {testimonials.map((testimonial, index) => (
                            <div key={index} className="bg-orange-900/50 p-8 rounded-lg">
                                <div className="flex gap-1 mb-4">
                                    {[...Array(testimonial.rating)].map((_, i) => (
                                        <Star key={i} className="w-5 h-5 fill-orange-400 text-orange-400" />
                                    ))}
                                </div>
                                <p className="text-orange-100 leading-relaxed mb-6">
                                    "{testimonial.text}"
                                </p>
                                <div className="flex items-center gap-3 pt-4 border-t border-orange-800">
                                    <div className="w-12 h-12 bg-orange-600 rounded-full flex items-center justify-center">
                                        <span className="text-white font-bold">{testimonial.name[0]}</span>
                                    </div>
                                    <div>
                                        <p className="font-bold text-white">{testimonial.name}</p>
                                        <p className="text-orange-400 text-sm">{testimonial.location}</p>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ===== NEWSLETTER ===== */}
            {/* Mobile: stacked */}
            <section className="lg:hidden py-10 bg-gradient-to-br from-orange-100 via-amber-50 to-orange-50">
                <div className="px-5 text-center">
                    <Zap className="w-10 h-10 text-orange-500 mx-auto mb-4" />
                    <h2 className="text-2xl font-black text-orange-950 mb-3">Stay In The Loop</h2>
                    <p className="text-orange-700 text-[14px] mb-6">
                        Get early access to drops, exclusive offers, and street style tips. Plus, 15% off your first order!
                    </p>
                    <div className="flex flex-col gap-3 max-w-sm mx-auto">
                        <input
                            type="email"
                            placeholder="Enter your email"
                            className="px-5 py-3.5 bg-white border-2 border-orange-200 text-orange-950 placeholder-orange-400 rounded-xl focus:outline-none focus:border-orange-500 transition-colors text-sm"
                        />
                        <button className="w-full px-8 py-3.5 bg-orange-600 text-white font-bold tracking-wider uppercase rounded-xl">
                            Subscribe
                        </button>
                    </div>
                </div>
            </section>

            {/* Desktop: Newsletter - EXACT ORIGINAL */}
            <section className="hidden lg:block py-16 lg:py-24 bg-gradient-to-br from-orange-100 via-amber-50 to-orange-50">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <Zap className="w-12 h-12 text-orange-500 mx-auto mb-6" />
                    <h2 className="text-4xl lg:text-5xl font-black text-orange-950 mb-4">
                        Stay In The Loop
                    </h2>
                    <p className="text-orange-700 text-lg mb-8">
                        Get early access to drops, exclusive offers, and street style tips.
                        Plus, 15% off your first order!
                    </p>
                    <div className="flex flex-col sm:flex-row gap-3 max-w-lg mx-auto">
                        <input
                            type="email"
                            placeholder="Enter your email"
                            className="flex-1 px-6 py-4 bg-white border-2 border-orange-200 text-orange-950 placeholder-orange-400 focus:outline-none focus:border-orange-500 transition-colors"
                        />
                        <button className="px-8 py-4 bg-orange-600 text-white font-bold tracking-wider uppercase hover:bg-orange-700 transition-colors whitespace-nowrap">
                            Subscribe
                        </button>
                    </div>
                </div>
            </section>

            {/* ===== INSTAGRAM ===== */}
            <section className="py-12 lg:py-24 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="text-center mb-8 lg:mb-12">
                        <div className="flex items-center justify-center gap-3 mb-3">
                            <Instagram className="w-6 h-6 text-orange-600" />
                            <span className="text-orange-600 font-bold">@venue_streetwear</span>
                        </div>
                        <h2 className="text-2xl lg:text-4xl font-black text-orange-950">#VenueVibes</h2>
                    </div>

                    {/* Mobile: 3-col tight grid */}
                    <div className="grid grid-cols-3 gap-0.5 lg:hidden">
                        {[1, 2, 3, 4, 5, 6].map((_, index) => (
                            <div key={index} className="aspect-square overflow-hidden">
                                <img
                                    src={`https://placehold.co/300x300/FFF7ED/EA580C?text=Street+${index + 1}`}
                                    alt={`Instagram post ${index + 1}`}
                                    className="w-full h-full object-cover"
                                />
                            </div>
                        ))}
                    </div>
                    {/* Desktop: 6-col with hover */}
                    <div className="hidden lg:grid grid-cols-3 md:grid-cols-6 gap-2 lg:gap-4">
                        {[1, 2, 3, 4, 5, 6].map((_, index) => (
                            <div key={index} className="group relative aspect-square overflow-hidden cursor-pointer">
                                <img
                                    src={`https://placehold.co/300x300/FFF7ED/EA580C?text=Street+${index + 1}`}
                                    alt={`Instagram post ${index + 1}`}
                                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                />
                                <div className="absolute inset-0 bg-orange-600/0 group-hover:bg-orange-600/40 transition-colors duration-300 flex items-center justify-center">
                                    <Heart className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ===== SERVICES ===== */}
            {/* Mobile: horizontal scroll */}
            <section className="lg:hidden py-8 bg-orange-50 border-t border-orange-100">
                <div className="px-5">
                    <div className="flex overflow-x-auto gap-4 snap-x snap-mandatory scrollbar-hide">
                        {[
                            { icon: Truck, title: 'Free Shipping', desc: 'Orders \u20B9999+' },
                            { icon: RefreshCw, title: 'Easy Returns', desc: '15-day policy' },
                            { icon: Shield, title: 'Secure Payment', desc: '100% safe' },
                            { icon: Zap, title: 'COD Available', desc: 'Pan India' },
                        ].map((service, i) => (
                            <div key={i} className="flex-shrink-0 w-[55vw] snap-start flex items-center gap-3">
                                <div className="w-10 h-10 bg-orange-600 rounded flex items-center justify-center flex-shrink-0">
                                    <service.icon className="w-4 h-4 text-white" />
                                </div>
                                <div>
                                    <p className="font-bold text-orange-950 text-sm">{service.title}</p>
                                    <p className="text-orange-600 text-xs">{service.desc}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Desktop: Services - EXACT ORIGINAL */}
            <section className="hidden lg:block py-12 bg-orange-50 border-t border-orange-100">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-orange-600 rounded flex items-center justify-center">
                                <Truck className="w-5 h-5 text-white" />
                            </div>
                            <div>
                                <p className="font-bold text-orange-950">Free Shipping</p>
                                <p className="text-orange-600 text-sm">Orders {'\u20B9'}999+</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-orange-600 rounded flex items-center justify-center">
                                <RefreshCw className="w-5 h-5 text-white" />
                            </div>
                            <div>
                                <p className="font-bold text-orange-950">Easy Returns</p>
                                <p className="text-orange-600 text-sm">15-day policy</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-orange-600 rounded flex items-center justify-center">
                                <Shield className="w-5 h-5 text-white" />
                            </div>
                            <div>
                                <p className="font-bold text-orange-950">Secure Payment</p>
                                <p className="text-orange-600 text-sm">100% safe</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-4">
                            <div className="w-12 h-12 bg-orange-600 rounded flex items-center justify-center">
                                <Zap className="w-5 h-5 text-white" />
                            </div>
                            <div>
                                <p className="font-bold text-orange-950">COD Available</p>
                                <p className="text-orange-600 text-sm">Pan India</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Template12;
